"use client";

import { LoyaltyAccount } from "@/generated/email_parser.openapi";
import {
  AdditionalRateResponseV3,
  HotelDetails,
  HotelDetailsV3,
  HotelReviewItem,
  RawRateSummary,
  Review,
  RoomSummaryV3,
} from "@/generated/search.openapi";

import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  createContext,
  useContext,
  useState,
} from "react";
import { toast } from "react-toastify";
import { searchAndBookTestEnv, handleError } from "@/lib/apiUtils";
import { getDateFormat } from "@/lib/utils";
import {
  HotelRateValidationPostRequest,
  RateType,
  RateValidationResponse,
  V2HotelHotelIdBookPostRequest,
  HotelBookingResponse,
} from "@/generated/search.openapi";
import { usePostHog } from "posthog-js/react";
import { useGlobalStore } from "@/app/context/globalStoreProvider";
import { getCustomerIp } from "@/lib/booking_utils";

export const DEFAULT_BOOKING_ERROR_MESSAGE =
  "Our apologies, we encountered an error while booking your stay. Please try again. Contact support@gondola.ai and our team will help you out if the problem persists.";

export const CVV_ERROR_MESSAGES = {
  DEFAULT:
    "We couldn't verify your card's security code. Please check your card details and try again. If the problem persists, please contact support@gondola.ai.",
  INCOMPLETE_DATA: "Payment method data is incomplete. Please try again.",
  TIMEOUT: "The request timed out. Please try again.",
} as const;

export type ContactInfo = {
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber?: string;
  loyaltyId?: string;
  loyaltyStatus?: string;
};

export type BookingInfo = {
  checkinStr: string;
  checkoutStr: string;
  numberOfAdults: number;
  childrenAges: number[];
  room: RoomSummaryV3;
  rate: RawRateSummary;
  bookingToken: string;
  bookingId?: string;
};

type ReviewDetails = {
  review: (HotelReviewItem | Review)[];
};

type BookingContextType = {
  hotelDetails?: HotelDetailsV3 | HotelDetails;
  setHotelDetails?: Dispatch<
    SetStateAction<HotelDetailsV3 | HotelDetails | undefined>
  >;
  bookingInfo?: BookingInfo;
  setBookingInfo?: Dispatch<SetStateAction<BookingInfo | undefined>>;
  bookingInfoLoading?: boolean;
  setBookingInfoLoading?: Dispatch<SetStateAction<boolean>>;
  reviewDetails?: ReviewDetails;
  setReviewDetails?: Dispatch<SetStateAction<ReviewDetails | undefined>>;
  selectedLoyaltyAccount?: LoyaltyAccount;
  setSelectedLoyaltyAccount?: Dispatch<
    SetStateAction<LoyaltyAccount | undefined>
  >;
  rateResponse?: AdditionalRateResponseV3;
  setRateResponse?: Dispatch<
    SetStateAction<AdditionalRateResponseV3 | undefined>
  >;
};

const BookingContext = createContext<BookingContextType>({});

export default function BookingProvider({ children }: PropsWithChildren) {
  const [hotelDetails, setHotelDetails] = useState<
    HotelDetailsV3 | HotelDetails
  >();
  const [bookingInfo, setBookingInfo] = useState<BookingInfo>();
  const [reviewDetails, setReviewDetails] = useState<ReviewDetails>();
  const [bookingInfoLoading, setBookingInfoLoading] = useState<boolean>(false);
  const [selectedLoyaltyAccount, setSelectedLoyaltyAccount] =
    useState<LoyaltyAccount>();
  const [rateResponse, setRateResponse] = useState<AdditionalRateResponseV3>();
  return (
    <BookingContext.Provider
      value={{
        hotelDetails,
        setHotelDetails,
        bookingInfo,
        setBookingInfo,
        reviewDetails,
        setReviewDetails,
        bookingInfoLoading,
        setBookingInfoLoading,
        selectedLoyaltyAccount,
        setSelectedLoyaltyAccount,
        rateResponse,
        setRateResponse,
      }}
    >
      {children}
    </BookingContext.Provider>
  );
}

const useBooking = () => {
  const posthog = usePostHog();
  const { refetchDataPostBooking } = useGlobalStore();

  const {
    hotelDetails,
    setHotelDetails,
    bookingInfo,
    setBookingInfo,
    reviewDetails,
    setReviewDetails,
    bookingInfoLoading,
    setBookingInfoLoading,
    selectedLoyaltyAccount,
    setSelectedLoyaltyAccount,
    rateResponse,
    setRateResponse,
  } = useContext(BookingContext);

  const fetchPriceCheckV2 = async (loyaltyId?: string) => {
    try {
      const rate = bookingInfo?.rate;
      const room = bookingInfo?.room;
      const childrenAges = bookingInfo?.childrenAges || [];

      if (!bookingInfo || !rate || !room || !hotelDetails) {
        throw new Error("Missing required booking information");
      }

      const request: HotelRateValidationPostRequest = {
        rateValidationRequest: {
          checkin: getDateFormat(bookingInfo.checkinStr as string),
          checkinStr: bookingInfo.checkinStr,
          checkout: getDateFormat(bookingInfo.checkoutStr as string),
          checkoutStr: bookingInfo.checkoutStr,
          rateId: rate.rateId as string,
          rateType: rate.rateType as RateType,
          vervotechPropertyId: hotelDetails.hotelSummary
            .vervotechPropertyId as number,
          rateToken: rate.rateToken as string,
          roomId: Number(room.roomId),
          occupancy: `${bookingInfo.numberOfAdults as number}${childrenAges.length ? "-" + childrenAges.join(",") : ""}`,
          loyaltyAccountId: loyaltyId,
          test: searchAndBookTestEnv,
        },
      };

      const response = await fetch("/api/hotel/priceCheck/v2", {
        method: "POST",
        body: JSON.stringify(request),
      });

      const roomRate = (await response.json()) as RateValidationResponse;
      return roomRate;
    } catch (error) {
      toast.error((error as Error).message);
      throw error;
    }
  };

  const refetchPriceCheck = async (loyaltyId?: string) => {
    try {
      setBookingInfoLoading?.(true);
      const v2Response = await fetchPriceCheckV2(loyaltyId);

      if (v2Response.rateSummary && v2Response.bookingToken) {
        const rate = v2Response.rateSummary as RawRateSummary;
        setBookingInfo?.((prev) => ({
          ...(prev as BookingInfo),
          rate: {
            ...rate,
            cancellationPolicyFull:
              rate.rateType === RateType.DirectCash ||
              rate.rateType === RateType.DirectPoints
                ? rate.cancellationPolicyFull
                : rate.cancellationPolicyFull,
          },
          bookingToken: v2Response.bookingToken!,
        }));
      } else {
        throw new Error("Error while booking, please try again later.");
      }
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setBookingInfoLoading?.(false);
    }
  };

  const bookThroughAPI = async (
    contactInfo: ContactInfo,
    paymentMethodId: string,
    useGondolaCredits: boolean,
    specialRequest?: string,
    loyaltyId?: string,
    eventId?: string
  ): Promise<{ bookingId: string }> => {
    if (!bookingInfo || !hotelDetails) {
      throw new Error(DEFAULT_BOOKING_ERROR_MESSAGE);
    }

    try {
      const ip = (await getCustomerIp()) || "";
      const vervotechPropertyId =
        hotelDetails.hotelSummary.vervotechPropertyId?.toString() || "";

      const bookingRequest: V2HotelHotelIdBookPostRequest = {
        hotelId: vervotechPropertyId,
        bookingRequest: {
          loyaltyAccountId: loyaltyId,
          test: searchAndBookTestEnv,
          specialRequest,
          bookingToken: bookingInfo.bookingToken ?? "",
          customerIp: ip,
          emailAddress: contactInfo.emailAddress,
          givenName: contactInfo.firstName,
          familyName: contactInfo.lastName,
          phoneNumber: contactInfo.phoneNumber ?? "",
          paymentId: paymentMethodId,
          rateType: bookingInfo.rate.rateType as RateType,
          useAvailableCredits: useGondolaCredits,
          scheduledEventCode: eventId,
        },
      };

      const response = await fetch("/api/hotel/book", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(bookingRequest),
      });

      const bookingResponse = (await response.json()) as HotelBookingResponse;

      if (bookingResponse.statusCode !== 200) {
        throw new Error(
          bookingResponse.error?.message ?? DEFAULT_BOOKING_ERROR_MESSAGE
        );
      }

      if (!bookingResponse.booking?.gondolaBookingId) {
        throw new Error(
          "Booking was successful but no booking ID was returned"
        );
      }

      refetchDataPostBooking();
      return {
        bookingId: bookingResponse.booking.gondolaBookingId,
      };
    } catch (error) {
      posthog.capture("Booking failed", {
        error: (error as Error)?.message ?? "Unknown error",
      });
      throw error;
    }
  };

  const handleBookingSubmission = async (
    contactInfo: ContactInfo,
    paymentMethodId: string,
    useGondolaCredits: boolean,
    specialRequest?: string,
    loyaltyId?: string,
    eventId?: string
  ) => {
    try {
      // Submit booking
      const { bookingId } = await bookThroughAPI(
        contactInfo,
        paymentMethodId,
        useGondolaCredits,
        specialRequest,
        loyaltyId,
        eventId
      );

      if (!bookingId) {
        throw new Error(DEFAULT_BOOKING_ERROR_MESSAGE);
      }
      return bookingId;
    } catch (error) {
      const errorMessage = (error as Error)?.message
        ? `Our apologies, we encountered an error: ${(error as Error).message}. If this issue persists, please contact support@gondola.ai.`
        : DEFAULT_BOOKING_ERROR_MESSAGE;

      handleError(errorMessage, { reportError: true, silent: true });
      throw { error, errorMessage };
    }
  };

  return {
    hotelDetails,
    setHotelDetails,
    bookingInfo,
    setBookingInfo,
    reviewDetails,
    setReviewDetails,
    bookingInfoLoading,
    setBookingInfoLoading,
    selectedLoyaltyAccount,
    setSelectedLoyaltyAccount,
    rateResponse,
    setRateResponse,
    fetchPriceCheckV2,
    refetchPriceCheck,
    handleBookingSubmission,
  };
};

export { useBooking };
